import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import EmergencyModal from './EmergencyModal';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResidentialOpen, setIsResidentialOpen] = useState(false);
  const [isTestingOpen, setIsTestingOpen] = useState(false);
  const [isRepairsOpen, setIsRepairsOpen] = useState(false);
  const [isLightingOpen, setIsLightingOpen] = useState(false);
  const [isCommercialOpen, setIsCommercialOpen] = useState(false);
  const [isSmartHomeOpen, setIsSmartHomeOpen] = useState(false);
  const [isRenewablesOpen, setIsRenewablesOpen] = useState(false);
  const [isFireSafetyOpen, setIsFireSafetyOpen] = useState(false);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const residentialServices = [
    { name: 'Full House Rewiring', path: '/services/residential-electrical/full-house-rewiring' },
    { name: 'Partial Rewiring & Circuit Upgrades', path: '/services/residential-electrical/partial-rewiring' },
    { name: 'Panel Upgrades', path: '/services/residential-electrical/panel-upgrades' },
    { name: 'New Circuit Installations', path: '/services/residential-electrical/new-circuits' },
    { name: 'Kitchen Electrical', path: '/services/residential-electrical/kitchen-electrical' },
    { name: 'Bathroom Electrical', path: '/services/residential-electrical/bathroom-electrical' },
    { name: 'Appliance Circuits', path: '/services/residential-electrical/appliance-circuits' },
    { name: 'Ceiling Fan Installation', path: '/services/residential-electrical/ceiling-fan-installation' },
    { name: 'Smoke Detector Installation', path: '/services/residential-electrical/smoke-detector-installation' },
    { name: 'Carbon Monoxide Detector Installation', path: '/services/residential-electrical/carbon-monoxide-detector-installation' },
    { name: 'Home Surge Protection', path: '/services/residential-electrical/home-surge-protection' },
    { name: 'Home Renovation Electrical', path: '/services/residential-electrical/home-renovation-electrical' },
    { name: 'New Home Build Electrical', path: '/services/residential-electrical/new-home-electrical' },
    { name: 'Garage Electrical', path: '/services/residential-electrical/garage-electrical' },
    { name: 'Home Electrical Inspection', path: '/services/residential-electrical/home-electrical-inspection' },
  ];

  const testingServices = [
    { name: 'Residential Electrical Safety Inspections', path: '/services/testing-certification/residential-electrical-safety-inspections' },
    { name: 'Pre-Purchase Electrical Inspections', path: '/services/testing-certification/pre-purchase-electrical-inspections' },
    { name: 'Landlord Electrical Safety Checks', path: '/services/testing-certification/landlord-electrical-safety-checks' },
    { name: 'Code Compliance Inspections', path: '/services/testing-certification/code-compliance-inspections' },
    { name: 'Insurance Electrical Inspections', path: '/services/testing-certification/insurance-electrical-inspections' },
    { name: 'Electrical Fault Diagnostics', path: '/services/testing-certification/electrical-fault-diagnostics' },
    { name: 'Circuit Load Testing', path: '/services/testing-certification/circuit-load-testing' },
    { name: 'Breaker Panel Testing & Inspection', path: '/services/testing-certification/breaker-panel-testing' },
    { name: 'Wiring Condition Inspections', path: '/services/testing-certification/wiring-condition-inspections' },
    { name: 'Thermal Imaging Electrical Inspections', path: '/services/testing-certification/thermal-imaging-electrical-inspections' },
  ];

  const repairsServices = [
    { name: 'Emergency Electrical Repairs', path: '/services/repairs-maintenance/emergency-electrical-repairs' },
    { name: 'Electrical Fault Finding & Diagnostics', path: '/services/repairs-maintenance/electrical-fault-finding' },
    { name: 'Tripping Breaker Repairs', path: '/services/repairs-maintenance/tripping-breaker-repairs' },
    { name: 'Dead Power Outlet Repairs', path: '/services/repairs-maintenance/dead-outlet-repairs' },
    { name: 'Flickering Light Repairs', path: '/services/repairs-maintenance/flickering-light-repairs' },
    { name: 'Burning Smell Investigation', path: '/services/repairs-maintenance/burning-smell-investigation' },
    { name: 'Electrical Wiring Repairs', path: '/services/repairs-maintenance/electrical-wiring-repairs' },
    { name: 'Circuit Breaker Repairs', path: '/services/repairs-maintenance/circuit-breaker-repairs' },
    { name: 'Damaged Outlet & Switch Repairs', path: '/services/repairs-maintenance/damaged-outlet-switch-repairs' },
    { name: 'Electrical Maintenance Plans', path: '/services/repairs-maintenance/electrical-maintenance-plans' },
  ];

  const lightingServices = [
    { name: 'Interior Lighting Installation', path: '/services/lighting-outdoor/interior-lighting-installation' },
    { name: 'Recessed / Downlight Installation', path: '/services/lighting-outdoor/recessed-downlight-installation' },
    { name: 'LED Lighting Upgrades', path: '/services/lighting-outdoor/led-lighting-upgrades' },
    { name: 'Kitchen Under-Cabinet Lighting', path: '/services/lighting-outdoor/kitchen-under-cabinet-lighting' },
    { name: 'Pendant Lighting Installation', path: '/services/lighting-outdoor/pendant-lighting-installation' },
    { name: 'Security Lighting Installation', path: '/services/lighting-outdoor/security-lighting-installation' },
    { name: 'Motion Sensor Lighting', path: '/services/lighting-outdoor/motion-sensor-lighting' },
    { name: 'Landscape Lighting Installation', path: '/services/lighting-outdoor/landscape-lighting-installation' },
    { name: 'Garden & Pathway Lighting', path: '/services/lighting-outdoor/garden-pathway-lighting' },
    { name: 'Outdoor Power Outlet Installation', path: '/services/lighting-outdoor/outdoor-power-outlet-installation' },
    { name: 'Patio & Deck Lighting', path: '/services/lighting-outdoor/patio-deck-lighting' },
    { name: 'Parking Lot Lighting Installation', path: '/services/lighting-outdoor/parking-lot-lighting-installation' },
    { name: 'Floodlight Installation', path: '/services/lighting-outdoor/floodlight-installation' },
  ];

  const commercialServices = [
    { name: 'Commercial Electrical Installations', path: '/services/commercial-specialist/commercial-electrical-installations' },
    { name: 'Commercial Electrical Maintenance', path: '/services/commercial-specialist/commercial-electrical-maintenance' },
    { name: 'Office Electrical Fit-Outs', path: '/services/commercial-specialist/office-electrical-fit-outs' },
    { name: 'Retail Store Electrical', path: '/services/commercial-specialist/retail-store-electrical' },
    { name: 'Restaurant Electrical Systems', path: '/services/commercial-specialist/restaurant-electrical-systems' },
    { name: 'Warehouse Electrical', path: '/services/commercial-specialist/warehouse-electrical' },
    { name: 'Three-Phase Power', path: '/services/commercial-specialist/three-phase-power' },
    { name: 'Commercial Lighting Systems', path: '/services/commercial-specialist/commercial-lighting-systems' },
    { name: 'Emergency Lighting', path: '/services/commercial-specialist/emergency-lighting' },
    { name: 'Commercial Panel Upgrades', path: '/services/commercial-specialist/commercial-panel-upgrades' },
    { name: 'New Commercial Builds', path: '/services/commercial-specialist/new-commercial-builds' },
    { name: 'Property Developers', path: '/services/commercial-specialist/property-developers' },
  ];

  const smartHomeServices = [
    { name: 'Smart Lighting Installation', path: '/services/smart-home-energy/smart-lighting-installation' },
    { name: 'Smart Switch Installation', path: '/services/smart-home-energy/smart-switch-installation' },
    { name: 'Smart Thermostat Installation', path: '/services/smart-home-energy/smart-thermostat-installation' },
    { name: 'Home Automation Systems', path: '/services/smart-home-energy/home-automation-systems' },
    { name: 'Smart Security Systems', path: '/services/smart-home-energy/smart-security-systems' },
    { name: 'Energy Monitoring Systems', path: '/services/smart-home-energy/energy-monitoring-systems' },
    { name: 'Whole-Home Energy Management', path: '/services/smart-home-energy/whole-home-energy-management' },
    { name: 'Smart Doorbell Installation', path: '/services/smart-home-energy/smart-doorbell-installation' },
    { name: 'Smart Appliance Integration', path: '/services/smart-home-energy/smart-appliance-integration' },
    { name: 'Smart Home Setup & Configuration', path: '/services/smart-home-energy/smart-home-setup-configuration' },
  ];

  const renewablesServices = [
    { name: 'EV Charger Installation', path: '/services/renewables-ev/ev-charger-installation' },
    { name: 'Tesla Charger Installation', path: '/services/renewables-ev/tesla-charger-installation' },
    { name: 'Home EV Charging Stations', path: '/services/renewables-ev/home-ev-charging-stations' },
    { name: 'Commercial EV Charger Installations', path: '/services/renewables-ev/commercial-ev-charger-installations' },
    { name: 'Solar Panel Electrical Connections', path: '/services/renewables-ev/solar-panel-electrical-connections' },
    { name: 'Solar Inverter Installation', path: '/services/renewables-ev/solar-inverter-installation' },
    { name: 'Solar Battery Storage Installation', path: '/services/renewables-ev/solar-battery-storage-installation' },
    { name: 'Backup Battery Systems', path: '/services/renewables-ev/backup-battery-systems' },
    { name: 'Electrical Systems for Solar', path: '/services/renewables-ev/electrical-systems-solar-installations' },
    { name: 'Solar System Monitoring Setup', path: '/services/renewables-ev/solar-system-monitoring-setup' },
  ];

  const fireSafetyServices = [
    { name: 'Fire Alarm Installation', path: '/services/fire-safety/fire-alarm-installation' },
    { name: 'Fire Alarm Maintenance', path: '/services/fire-safety/fire-alarm-maintenance' },
    { name: 'Smoke Detector Systems', path: '/services/fire-safety/smoke-detector-systems' },
    { name: 'Carbon Monoxide Detection', path: '/services/fire-safety/carbon-monoxide-detection' },
    { name: 'Emergency Lighting Systems', path: '/services/fire-safety/emergency-lighting-systems' },
    { name: 'Fire Alarm Panel Installation', path: '/services/fire-safety/fire-alarm-panel-installation' },
    { name: 'Fire Alarm Testing & Certification', path: '/services/fire-safety/fire-alarm-testing-certification' },
    { name: 'Fire Alarm Upgrades', path: '/services/fire-safety/fire-alarm-upgrades' },
    { name: 'Fire Alarm Monitoring', path: '/services/fire-safety/fire-alarm-monitoring' },
    { name: 'Fire Safety Compliance', path: '/services/fire-safety/fire-safety-compliance' },
  ];

  return (
    <>
      {/* Main Navbar */}
      <nav className="fixed w-full top-0 z-50 bg-black shadow-2xl border-b border-gray-800">
        <div className="w-full px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/2ee0947c9c88164b430bbd11db9ffeb9.png"
                alt="Electrician Solutions Fresno"
                className="h-14 w-auto object-contain"
              />
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className={`text-sm font-semibold uppercase tracking-widest transition-colors hover:text-[#FFD700] ${location.pathname === '/' ? 'text-[#FFD700]' : 'text-white'}`}>
                Home
              </Link>

              <div className="relative group">
                <button className="text-sm font-semibold uppercase tracking-widest text-white hover:text-[#FFD700] transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer">
                  Services
                  <i className="ri-arrow-down-s-line"></i>
                </button>
                <div className="absolute top-full left-0 mt-2 w-64 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <div className="relative group/residential">
                    <Link
                      to="/services/residential-electrical"
                      className="flex items-center justify-between px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors first:rounded-t-lg"
                    >
                      Residential Electrical
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                    <div className="absolute left-full top-0 ml-1 w-72 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover/residential:opacity-100 group-hover/residential:visible transition-all duration-300">
                      {residentialServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="relative group/testing">
                    <Link
                      to="/services/testing-certification"
                      className="flex items-center justify-between px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors"
                    >
                      Testing & Certification
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                    <div className="absolute left-full top-0 ml-1 w-80 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover/testing:opacity-100 group-hover/testing:visible transition-all duration-300">
                      {testingServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="relative group/repairs">
                    <Link
                      to="/services/repairs-maintenance"
                      className="flex items-center justify-between px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors"
                    >
                      Repairs & Maintenance
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                    <div className="absolute left-full top-0 ml-1 w-72 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover/repairs:opacity-100 group-hover/repairs:visible transition-all duration-300">
                      {repairsServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="relative group/lighting">
                    <Link
                      to="/services/lighting-outdoor"
                      className="flex items-center justify-between px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors"
                    >
                      Lighting & Outdoor
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                    <div className="absolute left-full top-0 ml-1 w-72 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover/lighting:opacity-100 group-hover/lighting:visible transition-all duration-300">
                      {lightingServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="relative group/commercial">
                    <Link
                      to="/services/commercial-specialist"
                      className="flex items-center justify-between px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors"
                    >
                      Commercial Specialist
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                    <div className="absolute left-full top-0 ml-1 w-72 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover/commercial:opacity-100 group-hover/commercial:visible transition-all duration-300">
                      {commercialServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="relative group/smarthome">
                    <Link
                      to="/services/smart-home-energy"
                      className="flex items-center justify-between px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors"
                    >
                      Smart Home & Energy
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                    <div className="absolute left-full top-0 ml-1 w-72 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover/smarthome:opacity-100 group-hover/smarthome:visible transition-all duration-300">
                      {smartHomeServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="relative group/renewables">
                    <Link
                      to="/services/renewables-ev"
                      className="flex items-center justify-between px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors"
                    >
                      Renewables & EV
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                    <div className="absolute left-full top-0 ml-1 w-72 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover/renewables:opacity-100 group-hover/renewables:visible transition-all duration-300">
                      {renewablesServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="relative group/firesafety">
                    <Link
                      to="/services/fire-safety"
                      className="flex items-center justify-between px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors last:rounded-b-lg"
                    >
                      Fire Safety
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                    <div className="absolute left-full top-0 ml-1 w-72 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover/firesafety:opacity-100 group-hover/firesafety:visible transition-all duration-300">
                      {fireSafetyServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block px-6 py-3 text-sm text-gray-700 hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <Link to="/about" className={`text-sm font-semibold uppercase tracking-widest transition-colors hover:text-[#FFD700] ${location.pathname === '/about' ? 'text-[#FFD700]' : 'text-white'}`}>
                About
              </Link>
              <Link to="/contact" className={`text-sm font-semibold uppercase tracking-widest transition-colors hover:text-[#FFD700] ${location.pathname === '/contact' ? 'text-[#FFD700]' : 'text-white'}`}>
                Contact
              </Link>
            </div>

            <button
              onClick={() => setShowEmergencyModal(true)}
              className="hidden lg:flex items-center gap-2 bg-[#FFD700] text-black px-6 py-3 rounded-full font-bold text-xs uppercase tracking-widest hover:bg-[#FFC700] transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
            >
              <i className="ri-alarm-warning-line text-base"></i>
              24/7 Emergency
            </button>

            <button
              className="lg:hidden text-white text-2xl cursor-pointer p-1"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="lg:hidden mt-3 pb-4 border-t border-gray-700 pt-4">
              <Link to="/" className="block py-2 text-sm text-white hover:text-[#FFD700] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                Home
              </Link>

              <div className="py-2">
                <div className="text-[#FFD700] text-xs font-bold uppercase tracking-widest mb-2">Services</div>
                
                {/* Residential Electrical */}
                <div className="mb-2">
                  <button
                    onClick={() => setIsResidentialOpen(!isResidentialOpen)}
                    className="flex items-center justify-between w-full py-2 text-sm text-white hover:text-[#FFD700] transition-colors cursor-pointer"
                  >
                    <span>Residential Electrical</span>
                    <i className={`ri-arrow-${isResidentialOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {isResidentialOpen && (
                    <div className="pl-4 mt-1">
                      <Link
                        to="/services/residential-electrical"
                        className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Overview
                      </Link>
                      {residentialServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Testing & Certification */}
                <div className="mb-2">
                  <button
                    onClick={() => setIsTestingOpen(!isTestingOpen)}
                    className="flex items-center justify-between w-full py-2 text-sm text-white hover:text-[#FFD700] transition-colors cursor-pointer"
                  >
                    <span>Testing & Certification</span>
                    <i className={`ri-arrow-${isTestingOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {isTestingOpen && (
                    <div className="pl-4 mt-1">
                      <Link
                        to="/services/testing-certification"
                        className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Overview
                      </Link>
                      {testingServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Repairs & Maintenance */}
                <div className="mb-2">
                  <button
                    onClick={() => setIsRepairsOpen(!isRepairsOpen)}
                    className="flex items-center justify-between w-full py-2 text-sm text-white hover:text-[#FFD700] transition-colors cursor-pointer"
                  >
                    <span>Repairs & Maintenance</span>
                    <i className={`ri-arrow-${isRepairsOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {isRepairsOpen && (
                    <div className="pl-4 mt-1">
                      <Link
                        to="/services/repairs-maintenance"
                        className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Overview
                      </Link>
                      {repairsServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lighting & Outdoor */}
                <div className="mb-2">
                  <button
                    onClick={() => setIsLightingOpen(!isLightingOpen)}
                    className="flex items-center justify-between w-full py-2 text-sm text-white hover:text-[#FFD700] transition-colors cursor-pointer"
                  >
                    <span>Lighting & Outdoor</span>
                    <i className={`ri-arrow-${isLightingOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {isLightingOpen && (
                    <div className="pl-4 mt-1">
                      <Link
                        to="/services/lighting-outdoor"
                        className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Overview
                      </Link>
                      {lightingServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Commercial Specialist */}
                <div className="mb-2">
                  <button
                    onClick={() => setIsCommercialOpen(!isCommercialOpen)}
                    className="flex items-center justify-between w-full py-2 text-sm text-white hover:text-[#FFD700] transition-colors cursor-pointer"
                  >
                    <span>Commercial Specialist</span>
                    <i className={`ri-arrow-${isCommercialOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {isCommercialOpen && (
                    <div className="pl-4 mt-1">
                      <Link
                        to="/services/commercial-specialist"
                        className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Overview
                      </Link>
                      {commercialServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Smart Home & Energy */}
                <div className="mb-2">
                  <button
                    onClick={() => setIsSmartHomeOpen(!isSmartHomeOpen)}
                    className="flex items-center justify-between w-full py-2 text-sm text-white hover:text-[#FFD700] transition-colors cursor-pointer"
                  >
                    <span>Smart Home & Energy</span>
                    <i className={`ri-arrow-${isSmartHomeOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {isSmartHomeOpen && (
                    <div className="pl-4 mt-1">
                      <Link
                        to="/services/smart-home-energy"
                        className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Overview
                      </Link>
                      {smartHomeServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Renewables & EV */}
                <div className="mb-2">
                  <button
                    onClick={() => setIsRenewablesOpen(!isRenewablesOpen)}
                    className="flex items-center justify-between w-full py-2 text-sm text-white hover:text-[#FFD700] transition-colors cursor-pointer"
                  >
                    <span>Renewables & EV</span>
                    <i className={`ri-arrow-${isRenewablesOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {isRenewablesOpen && (
                    <div className="pl-4 mt-1">
                      <Link
                        to="/services/renewables-ev"
                        className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Overview
                      </Link>
                      {renewablesServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Fire Safety */}
                <div className="mb-2">
                  <button
                    onClick={() => setIsFireSafetyOpen(!isFireSafetyOpen)}
                    className="flex items-center justify-between w-full py-2 text-sm text-white hover:text-[#FFD700] transition-colors cursor-pointer"
                  >
                    <span>Fire Safety</span>
                    <i className={`ri-arrow-${isFireSafetyOpen ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  {isFireSafetyOpen && (
                    <div className="pl-4 mt-1">
                      <Link
                        to="/services/fire-safety"
                        className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Overview
                      </Link>
                      {fireSafetyServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="block py-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <Link to="/about" className="block py-2 text-sm text-white hover:text-[#FFD700] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                About
              </Link>
              <Link to="/contact" className="block py-2 text-sm text-white hover:text-[#FFD700] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                Contact
              </Link>
              <button
                onClick={() => { setShowEmergencyModal(true); setIsMobileMenuOpen(false); }}
                className="block w-full mt-4 text-center bg-[#FFD700] text-black px-6 py-3 rounded-full font-bold text-sm uppercase cursor-pointer whitespace-nowrap"
              >
                <i className="ri-alarm-warning-line mr-2"></i>
                24/7 Emergency
              </button>
            </div>
          )}
        </div>
      </nav>

      {showEmergencyModal && (
        <EmergencyModal onClose={() => setShowEmergencyModal(false)} />
      )}
    </>
  );
};

export default Navbar;